/** What the Help screen says — as data, so the same words are read by the page and by the search.
 *
 * **Every sentence here is a statement about the software, checkable against the code**, and the
 * ones that are not are marked as not. This is the page a customer was promised at onboarding —
 * *how my data is processed* — so the failure to avoid is the one the rest of this product spends
 * its effort on: a confident sentence about something that is not so. Three rules for editing it:
 *
 * 1. **A regulatory or legal position does not appear here.** Whether EmitIQ is a controller or a
 *    processor, which lawful basis applies, what the transfer position is and what the statutory
 *    rights are belong to counsel (B-02, B-28); `docs/legal/privacy-and-data-processing.md` marks
 *    them `[COUNSEL]` and so does this page — by saying they are not stated, rather than by
 *    leaving a gap that reads as nothing being owed.
 * 2. **What is not built is said, in the sentence that would otherwise imply it** — a personal
 *    request records a receipt, not verified handling; retention has no deletion job.
 * 3. **No sentence names a person, a mailbox or an hour.** A support contact is configuration
 *    (`contact.ts`), because inventing one is the thing this product refuses to print.
 *
 * Facts below were checked against the code on **2026-10-22** and re-read on **2026-10-30** (passkeys, outgoing email): the deterministic extractor and the
 * absence of any provider adapter (`modules/evidence/extraction.py`, ADR-0011), the redaction
 * refusal (`evidence/service.require_redacted`), Argon2id (`identity/passwords.py`), the truncated
 * address (`identity/sessions.py`), the retention schedule (`evidence/service.retention_date`,
 * B-03), and the page's content-security policy, which names no third-party origin.
 *
 * A sentence copied from `docs/legal/privacy-and-data-processing.md` is a second home for it. That
 * file is the draft pending counsel; this is the in-product statement of its **factual** half.
 * When one changes, the other must — the test that guards this is the one that asserts the two
 * unbuilt things are still said.
 */

import { NAV_SECTIONS, PLACES, ROLES, canUse } from "./routeTable";
import type { NavId, Role } from "./routeTable";

export interface HelpSection {
  /** Stable, for a link and for a test. */
  id: string;
  heading: string;
  /** Paragraphs and bullets, as plain strings: no markup, so search can read them as they are. */
  body: readonly string[];
  /** Rendered as a list rather than paragraphs. */
  list?: boolean;
}

export interface HelpTopic {
  /** The `?tab=` value, so a topic is linkable. */
  key: string;
  label: string;
  /** What the topic is about, for the tab's live region and the search result. */
  about: string;
  sections: readonly HelpSection[];
}

export const DATA_TOPIC: HelpTopic = {
  key: "data",
  label: "How your data is processed",
  about: "where your data is held, what reads it, who else touches it and how long it is kept",
  sections: [
    {
      id: "scope",
      heading: "What this page is",
      body: [
        "This page describes what the software does with the data you give it. It is a statement of how the product works, not a legal notice: who is responsible for your data in law, the lawful basis for each kind of processing, the rules on transfers and your statutory rights are for our legal advisers and are not stated here. They are not stated because they have not yet been reviewed, not because nothing is owed.",
        "It was last checked against the software on 30 October 2026.",
      ],
    },
    {
      id: "what-you-give",
      heading: "What you give EmitIQ",
      body: [
        "Utility bills, fuel invoices, meter readings and spreadsheets that you upload; the facts you enter about your organisation (legal entities, sites, meters and reporting periods); and the names and email addresses of the people you invite to your workspace.",
      ],
    },
    {
      id: "where",
      heading: "Where it is held",
      list: true,
      body: [
        "Region: Europe West 2 (AWS). The database, the stored documents and the backups are there, and nothing in the product copies them to another region.",
        "Encryption of documents: documents are stored in encrypted storage. A key is created for each organisation and every upload asks for it.",
        "Separation: workspace data uses database row-level security. Shared identity records and personal requests do not all use that workspace policy: the application checks the caller's authority on the personal profile, session and request routes. Database policies and application checks protect different boundaries; not every table is protected by workspace row-level security.",
      ],
    },
    {
      id: "reading",
      heading: "What reads your documents",
      body: [
        "No AI model reads your documents. A figure is read out of a document by a deterministic parser that looks for labelled values in the text of a PDF or a spreadsheet, and no document is sent to a model. The optional Ask assistant is separate, off by default and works from your figures, not your documents (Where things are, Ask).",
        "Adding an AI provider for reading documents would need a signed agreement with that provider, a transfer assessment where processing leaves the UK, and a notice to you first. None of that exists, so none of it is in use.",
      ],
    },
    {
      id: "what-the-parser-does",
      heading: "What the parser does and does not do",
      list: true,
      body: [
        "A scanned image is refused rather than guessed at. It is stored as evidence, but no figure is read from it, because a misread figure arrives with no sign that it is wrong.",
        "A spreadsheet formula is never evaluated. The parser reads the value the file stored, and says so when there is none.",
        "Nothing a document says is matched against your other records automatically. A meter reference or a supplier name is text, not a lookup.",
        "A person must review and accept every extracted figure before it can enter a report. There is no route from an unreviewed document into a figure.",
        "Before a document is read it must be marked as redacted of incidental personal data, such as a name or phone number on an invoice that has nothing to do with the emissions figure. An unredacted document is refused, not warned about.",
      ],
    },
    {
      id: "who-else",
      heading: "Who else handles it",
      body: [
        "Amazon Web Services provides the hosting: database, document storage, encryption keys, operational alerts and the route for the product's outgoing email (an invitation, a re-sent invitation, a password reset). Delivery of those messages is not guaranteed and no screen shows whether one arrived, so hand an invitation link over yourself. No analytics service, error-tracking service, AI provider or third-party chat widget is connected, and the page's security policy names only EmitIQ's own origins.",
        "One thing we state rather than leave you to assume: our own register of third parties records that an agreement with Amazon Web Services is required, and does not yet record that it has been signed.",
      ],
    },
    {
      id: "people",
      heading: "Personal data held about people",
      list: true,
      body: [
        "Each user's name and email address, so people can sign in and so the product can say who did what.",
        "A password, which is stored hashed (Argon2id) and cannot be read back.",
        "A second-factor secret and recovery codes, required for Admin and Approver accounts.",
        "Any passkeys you add under Settings: the name you gave each one, an identifier the device gave it and its public key. The private key never reaches EmitIQ. Passkeys are an addition to your password and are new, so keep your password and recovery codes.",
        "A sign-in history, which holds a digest of the email address rather than the address, and a shortened network address rather than the full one.",
        "Incidental personal data inside documents you upload, which must be redacted before the document is read.",
      ],
    },
    {
      id: "audit-trail",
      heading: "The audit trail holds no names",
      body: [
        "Every figure is recorded in an append-only audit trail. People appear in it as identifiers only, never as names or email addresses. That is what allows an immutable record and the erasure of a person to coexist.",
      ],
    },
    {
      id: "erasure",
      heading: "Erasing a person",
      body: [
        "Erasure replaces a person's name, email and any external identity, retires every credential and ends every session. The record of who verified a figure survives, anonymised, because deleting it would break the history of a figure that may already have been filed, and the audit trail is not rewritten because it never held their name. It is not something an administrator of this workspace can do: a person's account may be shared with other companies, so anonymising it globally is not one workspace's act. To remove somebody's access here, suspend it — their other workspaces and their sign-in are unaffected.",
        "You can record a request about your own data from \u201cYour data requests\u201d in the account menu. Be clear about what that does and does not do: it records a receipt, nothing more. What does not exist yet is an identity-verification step, an assessed handling route to an authorised person, and a response clock \u2014 and signing in is not an identity check. So a request still needs to reach us through the contact on the Contact tab, and the record is evidence that you asked rather than evidence that anything happened.",
      ],
    },
    {
      id: "retention",
      heading: "How long things are kept",
      list: true,
      body: [
        "Evidence documents: a retention date three years after the end of the period the document covers is recorded against each one. No law we have found sets an evidence-retention period for either regime, so three years is a default we chose, not a legal requirement.",
        "The audit trail is kept indefinitely.",
        "Automatic deletion of expired documents is not available yet. The date is recorded and nothing acts on it yet, so no document is deleted today.",
      ],
    },
    {
      id: "not-done",
      heading: "What the product does not do",
      list: true,
      body: [
        "It does not submit anything to a regulator. It prepares the document; you file it.",
        "It does not train any model on your data. There is no model in the product to train.",
        "It does not sell your data or share it for marketing.",
      ],
    },
    {
      id: "export",
      heading: "Getting your data out",
      body: [
        "The product can export an inventory, show the complete history of any figure, and record a request you make about your own data. Anonymising an account is not something this workspace can do; it is a separate, restricted operation. The rest of your rights as a data subject, and how they are exercised, are for our legal advisers to set out and are not described here.",
      ],
    },
  ],
};

export const READING_TOPIC: HelpTopic = {
  key: "reading",
  label: "Reading the screens",
  about: "what a figure rests on, and the difference between nothing here, not assessed and could not be loaded",
  sections: [
    {
      id: "receipts",
      heading: "Where a figure comes from",
      body: [
        "Every figure opens a receipt: its source (a document, or a basis a person stated where there is no document), the activity value, the emission factor with its publisher and edition, the method, the warming-potential set, the calculation, who verified or accepted it and its entry in the audit trail. On a scope page (Scope 1, Scope 2 or Scope 3), click the tonnes in a figure's row to open it. If a receipt cannot be produced, the figure is not shown.",
      ],
    },
    {
      id: "three-states",
      heading: "Nothing here, not assessed, and could not be loaded",
      list: true,
      body: [
        "Nothing here yet means the screen asked and the answer was empty. It usually says what to do next.",
        "Not assessed means the product has not measured something and says so, rather than showing a zero. Scope 3 is the usual example: an unassessed total is never drawn as 0.0.",
        "Could not be loaded means the screen asked and did not get an answer. Nothing was changed, and the empty space is not a statement about your company: use Try again, and if it persists tell us on the Contact tab.",
      ],
    },
    {
      id: "superseded",
      heading: "A figure that has been replaced",
      body: [
        "When a reading is corrected, the old figure is kept and marked as replaced, never hidden. Opening its receipt says it is no longer current.",
      ],
    },
    {
      id: "support-level",
      heading: "What the regulation screens are and are not",
      body: [
        "Filings prepare and issue the document for the regimes the product supports. Each filing shows its support level, read from the product's published list. The highest level any filing has is \u201ccomplete reporting workflow supported\u201d, which the screens word as full workflow, no e-filing: the product produces and issues a record, and nothing is sent to a regulator. You file it.",
        "A filing that has passed its checks is not a statement that it is lawfully fit to file: that remains a person's judgement. The Reports place is a different thing, a library of standard data reports, and is not a filing.",
      ],
    },
  ],
};

/** What each place is for, in one plain sentence - the only hand-written part of the "Where things
 * are" topic. **Typed by `NavId`**, so a place added to `routeTable.ts` without a sentence here fails
 * `tsc`; the tabs and who is offered each place are not written here at all, they are read from the
 * route table (`placesSections`), so the menu cannot change and leave this page describing the old one.
 * Every sentence is checked against the header comment of the screen it describes. */
const PURPOSE: Readonly<Record<NavId, string>> = {
  home: "A summary of the workspace: the total, what needs you, the bills waiting to be reviewed or missing, and where each filing stands. What it shows depends on your role.",
  actions:
    "The work queue, and the place the bell in the top bar opens. The bell's number is the work waiting on you: approvals that name you, tasks you own and bills to review. It is not a count of unread messages. Notifications is a tab here, with its own count of messages not yet read.",
  ask: "Questions in plain words, answered from your figures and records, not your documents. It is off until three things are true: the add-on is in your agreement, an Admin has connected a model of your own (Admin, Integrations, AI assistant; the key is write-only) and an Admin has switched it on. The screen names which is missing, and no provider is called from anywhere in the product today. An Auditor is never offered it.",
  upload:
    "Where a bill goes in: drop files and see what became of each one, including a refusal. Reading a file and reviewing what was read happen in Processing.",
  processing:
    "Bills on their way to becoming figures. To review: read and waiting for a person. In progress: stored and not yet read. Missing: sources you expect that have not arrived. Refused: files that could not be used, with the reason.",
  scope1:
    "Direct emissions (fuel burned on your sites, refrigerant top-ups): one table of figures, a month view and a data-quality view. Click a figure's tonnes to open its receipt.",
  scope2:
    "Purchased electricity. The location-based figure is shown; the market-based column appears when the period has market-based figures, or for you if the advanced view in My settings is on.",
  scope3:
    "The fifteen Scope 3 categories. Categories is where a person records whether each is relevant; a category nobody has assessed says Not assessed, never zero. Figures lists what the product can price, and Suppliers' share what suppliers' own answers account for.",
  documents:
    "Every stored document with its state, and Bulk edit for correcting many figures through a spreadsheet you export and send back.",
  vendors:
    "The parties that bill you, their accounts and your requests to suppliers. A supplier is another company's own workspace; a vendor is your record of who bills you.",
  facilities:
    "Your sites, the companies that file and how they are owned. Adding or correcting a company is an Admin's act.",
  filings:
    "One tab per supported regime, each showing how far the filing has got, what is still to do and the issued records. Past filings holds figures you published before using the product.",
  reports: "A library of standard data reports. It is not a filing and shows no filing status.",
  dashboards:
    "Four fixed views of what the workspace holds: Executive, Data health, Operations and Suppliers.",
  targets: "Targets, recommendations, initiatives and the climate risk register.",
  audit:
    "Period close, the audit ledger, Activity and Assurance. An Auditor's first tab is Engagement, which states what their access covers.",
  admin:
    "The company Admin's place: people and roles, approval routes, security, integrations (including the AI assistant), the company, data (governance, methods and the support level of each framework), billing and usage, and the Admin log.",
  account:
    "Your own pages, from the menu under your name: My profile (name, password, passkeys, sessions), My settings (appearance, the Scope 2 view and notification choices) and Your data requests.",
  support:
    "Support (how to reach us, and whether this service is answering) and Help, which is this page. The question mark in the top bar opens Help at the place you are in.",
};

const ROLE_WORD: Readonly<Record<Role, string>> = {
  admin: "Admin",
  approver: "Approver",
  contributor: "Contributor",
  viewer: "Viewer",
  auditor: "Auditor",
};

/** The roles a place or tab is limited to, in words; `undefined` when every role is offered it. */
function whoWord(roles: readonly Role[] | undefined): string | undefined {
  if (roles === undefined || ROLES.every((role) => roles.includes(role))) return undefined;
  return roles.map((role) => ROLE_WORD[role]).join(", ");
}

/** One section per place, read from the route table: the sentence above, then the tabs (each with
 * the roles it is limited to, when it is narrower than the place), then who is offered the place. */
function placesSections(): readonly HelpSection[] {
  return NAV_SECTIONS.map((section) => {
    const tabs = PLACES.filter((place) => place.nav === section.id && place.tab !== undefined);
    const names = [...new Set(tabs.map((place) => place.tab as string))];
    const tabLine = names.length
      ? `Tabs: ${names
          .map((name) => {
            const limit = tabs
              .filter((place) => place.tab === name && place.roles !== undefined)
              .map((place) => whoWord(place.roles))
              .find((word): word is string => word !== undefined && whoWord(section.roles) !== word);
            return limit ? `${name} (${limit})` : name;
          })
          .join(", ")}.`
      : undefined;
    const who = whoWord(section.roles);
    return {
      id: `place-${section.id}`,
      heading: section.label,
      body: [PURPOSE[section.id], ...(tabLine ? [tabLine] : []), ...(who ? [`Offered to: ${who}.`] : [])],
    };
  });
}

export const PLACES_TOPIC: HelpTopic = {
  key: "places",
  label: "Where things are",
  about: "what each place in the menu is for, its tabs and who is offered it",
  sections: [
    {
      id: "menu",
      heading: "How the menu is arranged",
      body: [
        "The menu follows a bill: Start, Collect, Emissions, Records, Report, Plan and System. A heading is a label, not a page. You are offered the places your role can use; the server still decides what you may do, whatever a screen shows.",
        "In the top bar the question mark opens this page at the place you are in, the bell opens Action items, and your name opens your own pages.",
      ],
    },
    ...placesSections(),
  ],
};

/** The section id a place's help lives at: the value the question mark carries as `?place=`. */
export function placeSectionId(nav: string): string {
  return `place-${nav}`;
}

export const ROLES_TOPIC: HelpTopic = {
  key: "roles",
  label: "Roles",
  about: "what each role can do, and what an Auditor can read",
  sections: [
    {
      id: "roles",
      heading: "The five roles",
      list: true,
      body: [
        "Admin: sets the workspace up (companies, sites, people, approval routes, financial facts), and can do what an Approver can. An Admin cannot erase a person.",
        "Approver: verifies or rejects documents, decides approvals, closes a period and issues a filing.",
        "Contributor: uploads, corrects an extracted field with a reason, and works tasks, notes and flags. Cannot verify.",
        "Viewer: reads figures, receipts and reports. Changes nothing.",
        "Auditor: reads inside an engagement and raises evidence requests. Changes nothing in the inventory.",
        "The menu offers a role the places it can use, and a control a role cannot use is absent or says who can. Admins and Approvers always sign in with an authenticator code; an Admin can require one of everyone.",
      ],
    },
    {
      id: "auditor",
      heading: "What an Auditor can read",
      list: true,
      body: [
        "An Auditor is invited by an Admin, never promoted from another role. The invitation fixes the engagement: a window of data, the filings they are engaged for and the last day they may sign in.",
        "The Engagement tab (under Approvals & audit) states which companies, which period and until when. A Read-only mark stays in the top bar.",
        "Inside the engagement an Auditor reads figures, receipts, records and filings, and may download a verification pack for an engaged filing. Outside it a record answers not found, never forbidden, so a refusal does not confirm the record exists.",
        "An Auditor raises evidence requests on the Assurance tab. The company answers; only the person who raised a request resolves or withdraws it.",
        "Not offered to an Auditor: Upload, Processing, Targets & plan, Admin, the whole audit ledger and Activity.",
        "Access ends on the last day the Admin set, or when an Admin suspends the membership. Restoring a suspended membership does not extend an expired engagement.",
      ],
    },
    {
      id: "auditor-kept",
      heading: "What is kept about an Auditor's work",
      body: [
        "An evidence request, every answer to it and who closed it are kept with the company's records, oldest first, and an answer amended later stays visible. The audit trail records that a request was raised, answered, resolved or withdrawn, by identifiers only: never the question, the answer or a name. The audit trail itself is kept indefinitely.",
        "Opening a document is not recorded.",
      ],
    },
  ],
};

export const TOPICS: readonly HelpTopic[] = [DATA_TOPIC, READING_TOPIC, PLACES_TOPIC, ROLES_TOPIC];

/** The key of the contact tab. Not a topic: it has no sections, it has a `ContactPanel`. */
export const CONTACT_KEY = "contact";

export const HELP_TABS: ReadonlyArray<{ key: string; label: string }> = [
  ...TOPICS.map((topic) => ({ key: topic.key, label: topic.label })),
  { key: CONTACT_KEY, label: "Contact" },
];

export const DEFAULT_HELP_TAB = DATA_TOPIC.key;

/** One searchable line: where it lives and what it says. */
export interface HelpHit {
  tab: string;
  topic: string;
  heading: string;
  text: string;
}

/** Every section of every topic, flattened, for the shell's search.
 *
 * Given a role, a place's section is left out when the menu does not offer that role the place - the
 * palette lists only the screens a role can open, and a search hit for the Admin place that a Viewer
 * cannot open would be the same hole by another door. (Help itself shows every place, with who is
 * offered it, because a reader asking "who can do this" needs to see the places they cannot use.) */
export function helpIndex(role?: string): readonly HelpHit[] {
  return TOPICS.flatMap((topic) =>
    topic.sections
      .filter((section) => {
        const place = NAV_SECTIONS.find((candidate) => placeSectionId(candidate.id) === section.id);
        return role === undefined || place === undefined || canUse(role, place.roles);
      })
      .map((section) => ({
        tab: topic.key,
        topic: topic.label,
        heading: section.heading,
        text: section.body.join(" "),
      })),
  );
}
